/* crm-universal.jsx — Reusable Phase 2 CRM components
   Provides: UniversalTimeline, CustomFieldsPanel, ArchiveRestoreBtn,
             EmptyState, SkeletonRow, AIEntityCard
   Loaded before legacy-overlays.jsx in main.jsx load order.
   All components self-register on window. */

const { useState: useStateU, useEffect: useEffectU, useMemo: useMemoU } = React;

/* ================================================================
   UNIVERSAL TIMELINE
   Consolidates PersonTimeline (date-grouped, colored icons) into one
   reusable component for People, Companies, and Deals.
   ================================================================ */
const ACT_ICON = {
  whatsapp: 'MessageCircle', deal: 'Target', email: 'Mail',
  note: 'StickyNote', call: 'Phone', meeting: 'Calendar',
  followup: 'Clock', reminder: 'Bell', activity: 'Activity',
  task: 'CheckSquare', document: 'File',
};
const ACT_BG = {
  whatsapp: '#E7F5EE', deal: '#EDF4FD', email: '#F3EDFB',
  note: '#FEF6E6', call: '#E7F5EE', meeting: '#EDF4FD',
  followup: '#FEF6E6', task: '#F0F0FF', document: '#F5F5F5',
};
const ACT_FG = {
  whatsapp: '#0E8C5A', deal: '#125AB0', email: '#6E3FBA',
  note: '#B27800', call: '#0E8C5A', meeting: '#125AB0',
  followup: '#B27800', task: '#4F46E5', document: '#64748B',
};

const _tlGroupKey = (time) => {
  const t = (time || '').toLowerCase();
  if (t.includes('just') || t.match(/^[12]m\b/)) return 'Just Now';
  if (t.includes('min') || t.includes('hr') || t.match(/^\d+h\b/)) return 'Today';
  if (t.includes('1d') || t.includes('yesterday')) return 'Yesterday';
  if (t.match(/[2-6]d/)) return 'This Week';
  return 'Earlier';
};

const UniversalTimeline = ({ items = [], compact = false }) => {
  const grouped = useMemoU(() => {
    const ORDER = ['Just Now', 'Today', 'Yesterday', 'This Week', 'Earlier'];
    const map = new Map();
    items.forEach(a => {
      const k = _tlGroupKey(a.time);
      const list = map.get(k) || [];
      list.push(a);
      map.set(k, list);
    });
    return ORDER.filter(k => map.has(k)).map(k => [k, map.get(k)]);
  }, [items]);

  if (!items.length) return (
    <div className="nx-empty" style={{ padding: compact ? 'var(--space-4) 0' : 'var(--space-5) 0' }}>
      <NexusIcon name="Activity" size={compact ? 16 : 20} />
      <span>No activity recorded yet.</span>
    </div>
  );

  if (compact) {
    return (
      <ol className="nx-timeline">
        {items.map(a => {
          const fg = ACT_FG[a.type] || 'var(--nx-text-muted)';
          const ic = ACT_ICON[a.type] || 'Activity';
          return (
            <li key={a.id} className="nx-timeline-item">
              <span className="nx-timeline-dot" style={{ background: fg }}>
                <NexusIcon name={ic} size={11} />
              </span>
              <div className="nx-timeline-body">
                <span className="nx-timeline-text"><strong>{a.who}</strong> {a.what}</span>
                {a.detail && <span className="nx-timeline-detail">{a.detail}</span>}
                <span className="nx-timeline-time">{a.time}</span>
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <div className="nx-ptl">
      {grouped.map(([date, acts]) => (
        <div key={date} className="nx-ptl-group">
          <div className="nx-ptl-date">{date}</div>
          {acts.map(a => {
            const bg = ACT_BG[a.type] || 'var(--nx-surface-alt)';
            const fg = ACT_FG[a.type] || 'var(--nx-text-muted)';
            const ic = ACT_ICON[a.type] || 'Activity';
            return (
              <div key={a.id} className="nx-ptl-card">
                <div className="nx-ptl-icon" style={{ background: bg, color: fg }}>
                  <NexusIcon name={ic} size={12} />
                </div>
                <div className="nx-ptl-body">
                  <div className="nx-ptl-primary">
                    <strong>{a.who}</strong> {a.what}
                  </div>
                  {a.detail && <div className="nx-ptl-detail">{a.detail}</div>}
                  <div className="nx-ptl-time">{a.time}</div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
window.UniversalTimeline = UniversalTimeline;

/* ================================================================
   CUSTOM FIELDS PANEL
   Loads field definitions + values from backend.
   Supports all CRM field types with type-specific inputs.
   ================================================================ */

/* Parse formula JSON for subtype metadata */
const _cfMeta = (field) => {
  try { return JSON.parse(field.formula || '{}'); } catch { return {}; }
};

/* Derive the rendering subtype from backend type + formula metadata */
const _cfSubtype = (field) => {
  const meta = _cfMeta(field);
  if (meta.subtype) return meta.subtype;
  const t = (field.type || '').toUpperCase();
  if (t === 'BOOLEAN')      return 'checkbox';
  if (t === 'SELECT')       return 'select';
  if (t === 'MULTI_SELECT') return 'multiselect';
  if (t === 'DATE')         return 'date';
  if (t === 'NUMBER')       return 'number';
  return 'text';
};

/* Read-only renderers keyed by subtype — extracted to keep CfValue complexity low */
const _cfRenderBool = (raw) => (
  <span className={'nx-cf-bool' + (raw === 'true' ? ' is-on' : '')}>{raw === 'true' ? 'Yes' : 'No'}</span>
);
const _cfRenderMulti = (raw) => {
  const tags = typeof raw === 'string' ? raw.split(',').filter(Boolean) : [];
  return <span className="nx-cf-tags">{tags.map(t => <span key={t} className="nx-tag">{t.trim()}</span>)}</span>;
};
const _cfRenderCurrency = (field, raw) => {
  const sym = (_cfMeta(field).currency === 'INR') ? '₹' : '$';
  const n = Number.parseFloat(raw);
  return <span className="nx-cf-mono">{Number.isNaN(n) ? raw : sym + n.toLocaleString()}</span>;
};
const _cfRenderPct = (raw) => {
  const n = Number.parseFloat(raw);
  return <span className="nx-cf-mono">{Number.isNaN(n) ? raw : n + '%'}</span>;
};

const _CF_RENDER = {
  checkbox:    (f, r) => _cfRenderBool(r),
  multiselect: (f, r) => _cfRenderMulti(r),
  currency:    (f, r) => _cfRenderCurrency(f, r),
  percentage:  (f, r) => _cfRenderPct(r),
  url:   (f, r) => <a href={r.startsWith('http') ? r : 'https://' + r} target="_blank" rel="noopener noreferrer" className="nx-cf-link">{r}</a>,
  email: (f, r) => <a href={'mailto:' + r} className="nx-cf-link">{r}</a>,
  phone: (f, r) => <a href={'tel:' + r} className="nx-cf-link">{r}</a>,
};

/* Render a single read-only value display */
const CfValue = ({ field, raw }) => {
  if (raw == null || raw === '') return <span className="nx-cf-empty">—</span>;
  const sub = _cfSubtype(field);
  const renderer = _CF_RENDER[sub];
  if (renderer) return renderer(field, raw);
  return <span className="nx-cf-text">{raw}</span>;
};

/**
 * CF-01: Read-only table cell for a single custom field value.
 * Infrastructure only — not integrated into any table (CF-02 scope).
 *
 * Props:
 *   fieldId  {string}        — OrgCustomField UUID
 *   defs     {Array}         — active field definitions for this entity type (from store.customFieldDefs)
 *   raw      {string|null}   — raw string value from the batch endpoint, or null/undefined
 */
export const CfTableCell = ({ fieldId, defs, raw }) => {
  try {
    const field = Array.isArray(defs) ? defs.find(d => d.id === fieldId) : undefined;
    if (!field) return <span className="nx-cf-empty">—</span>;
    if (raw == null || raw === '') return <span className="nx-cf-empty">—</span>;
    const sub = _cfSubtype(field);
    const renderer = _CF_RENDER[sub];
    if (renderer) return renderer(field, raw);
    return <span className="nx-cf-text">{raw}</span>;
  } catch {
    return <span className="nx-cf-empty">—</span>;
  }
};

if (typeof window !== 'undefined') window.CfTableCell = CfTableCell;

const _CF_INPUT_TYPE = {
  date: 'date', datetime: 'datetime-local',
  number: 'number', currency: 'number', percentage: 'number',
  email: 'email', phone: 'tel', url: 'url',
};
const _CF_PLACEHOLDER = { url: 'https://', email: 'name@example.com', phone: '+1 555 000 0000' };

/* Save / Cancel button pair — defined outside editors to avoid re-creation */
const CfSaveCancel = ({ onSave, onCancel }) => (
  <div className="nx-cf-editor-btns">
    <button type="button" className="nx-iconbtn" onClick={onSave} title="Save" aria-label="Save"><NexusIcon name="Check" size={12} /></button>
    <button type="button" className="nx-iconbtn" onClick={onCancel} title="Cancel" aria-label="Cancel"><NexusIcon name="X" size={12} /></button>
  </div>
);

/* Inline editor for a custom field */
const CfEditor = ({ field, raw, onSave, onCancel }) => {
  const sub = _cfSubtype(field);
  const [draft, setDraft] = useStateU(raw ?? '');
  const [selected, setSelected] = useStateU(
    sub === 'multiselect' && raw ? raw.split(',').filter(Boolean).map(s => s.trim()) : []
  );

  const commit = () => onSave(sub === 'multiselect' ? selected.join(',') : draft);

  const handleKey = (e) => {
    if (e.key === 'Enter' && sub !== 'textarea') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') onCancel();
  };

  if (sub === 'checkbox') {
    const checked = draft === 'true';
    return (
      <div className="nx-cf-editor">
        <label className="nx-cf-check-label">
          <input type="checkbox" checked={checked} onChange={e => setDraft(e.target.checked ? 'true' : 'false')} />
          <span>{checked ? 'Yes' : 'No'}</span>
        </label>
        <CfSaveCancel onSave={commit} onCancel={onCancel} />
      </div>
    );
  }

  if (sub === 'select') {
    return (
      <div className="nx-cf-editor">
        <select className="nx-cf-select" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={handleKey} autoFocus>
          <option value="">— Select —</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <CfSaveCancel onSave={commit} onCancel={onCancel} />
      </div>
    );
  }

  if (sub === 'multiselect') {
    const toggleOpt = (o) => setSelected(s => s.includes(o) ? s.filter(x => x !== o) : [...s, o]);
    return (
      <div className="nx-cf-editor nx-cf-editor--multi">
        <div className="nx-cf-multi-opts">
          {(field.options || []).map(o => (
            <button key={o} type="button"
              className={'nx-cf-opt-chip' + (selected.includes(o) ? ' is-on' : '')}
              onClick={() => toggleOpt(o)}>
              {selected.includes(o) && <NexusIcon name="Check" size={10} />}{o}
            </button>
          ))}
        </div>
        <CfSaveCancel onSave={commit} onCancel={onCancel} />
      </div>
    );
  }

  if (sub === 'textarea') {
    return (
      <div className="nx-cf-editor nx-cf-editor--full">
        <textarea className="nx-cf-textarea" autoFocus value={draft} rows={3}
          onChange={e => setDraft(e.target.value)} onKeyDown={handleKey} />
        <CfSaveCancel onSave={commit} onCancel={onCancel} />
      </div>
    );
  }

  return (
    <div className="nx-cf-editor">
      <input className="nx-cf-input" type={_CF_INPUT_TYPE[sub] || 'text'} autoFocus value={draft}
        onChange={e => setDraft(e.target.value)} onKeyDown={handleKey}
        placeholder={_CF_PLACEHOLDER[sub] || ''} />
      <CfSaveCancel onSave={commit} onCancel={onCancel} />
    </div>
  );
};

/* Single field row: label + value + edit trigger */
const CfRow = ({ field, raw, onSave, disabled }) => {
  const [editing, setEditing] = useStateU(false);
  const [rowError, setRowError] = useStateU(null);
  const sub = _cfSubtype(field);
  const typeLabel = {
    text: 'Text', textarea: 'Text Area', number: 'Number',
    currency: 'Currency', percentage: 'Percentage', date: 'Date',
    datetime: 'DateTime', checkbox: 'Checkbox', select: 'Select',
    multiselect: 'Multi Select', email: 'Email', phone: 'Phone', url: 'URL',
  }[sub] || field.type;

  const handleCommit = async (v) => {
    // Frontend guard: prevent submitting an empty required field (Case C)
    if (field.required && (v === '' || v == null)) {
      setRowError('This field is required.');
      return;
    }
    setRowError(null);
    const ok = await onSave(v);
    if (ok) setEditing(false);
    // Stay in edit mode on failure so the user can correct the value
  };

  return (
    <div className={'nx-cf-row' + (disabled ? ' is-saving' : '')}>
      <div className="nx-cf-row-head">
        <span className="nx-cf-label" title={typeLabel}>
          {field.label}
          {field.required && <span className="nx-cf-required" aria-label="required" title="Required field"> *</span>}
        </span>
        {!editing && (
          <button type="button" className="nx-iconbtn nx-cf-edit-btn" title="Edit" aria-label="Edit"
            onClick={() => { setEditing(true); setRowError(null); }}>
            <NexusIcon name="Pencil" size={11} />
          </button>
        )}
      </div>
      {editing ? (
        <>
          <CfEditor field={field} raw={raw}
            onSave={handleCommit}
            onCancel={() => { setEditing(false); setRowError(null); }} />
          {rowError && <span className="nx-cf-error">{rowError}</span>}
        </>
      ) : (
        <div className="nx-cf-value" onDoubleClick={() => { setEditing(true); setRowError(null); }}>
          <CfValue field={field} raw={raw} />
        </div>
      )}
    </div>
  );
};

/* Main panel */
const CustomFieldsPanel = ({ record, entityType }) => {
  const store = useStore();
  const wsId  = store.workspaceId || window.__nx_auth?.user?.activeWorkspaceId;
  const [defs,     setDefs]     = useStateU(null);   // null = loading
  const [values,   setValues]   = useStateU({});
  const [saving,   setSaving]   = useStateU(false);
  const [fetchErr, setFetchErr] = useStateU(null);
  const toast = useToast();

  useEffectU(() => {
    if (!wsId || !record?.id) return;
    setDefs(null);
    setFetchErr(null);
    Promise.all([
      window.__nx_api.customFields.getDefinitions(wsId, entityType)
        .then(r => Array.isArray(r) ? r : (r?.data ?? [])).catch(() => []),
      window.__nx_api.customFields.getValues(wsId, entityType, record.id)
        .then(r => Array.isArray(r) ? r : (r?.data ?? [])).catch(() => []),
    ]).then(([defs, vals]) => {
      setDefs(defs);
      const map = {};
      (vals || []).forEach(v => { map[v.fieldId] = v.value; });
      setValues(map);
    }).catch(err => {
      setFetchErr(err?.message || 'Failed to load custom fields');
      setDefs([]);
    });
  }, [wsId, record?.id, entityType]);

  const handleSave = async (fieldId, value) => {
    if (!wsId || !record?.id) return false;
    setSaving(true);
    let ok = false;
    try {
      const result = await window.__nx_api.customFields.setValues(
        wsId, entityType, record.id,
        [{ fieldId, value: value === '' ? null : String(value) }]
      );
      const map = {};
      (Array.isArray(result) ? result : []).forEach(v => { map[v.fieldId] = v.value; });
      setValues(prev => ({ ...prev, ...map, [fieldId]: value }));
      toast({ tone: 'success', icon: 'Check', title: 'Field saved' });
      ok = true;
    } catch (err) {
      // CF-001: surface required-field and other errors; CfRow stays in edit mode on failure.
      const data = err?.response?.data || err?.data;
      const desc = (data?.code === 'REQUIRED_FIELD_MISSING')
        ? (data?.message || 'Required field cannot be empty')
        : (err?.message || 'Could not save field');
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc });
    }
    setSaving(false);
    return ok;
  };

  /* Loading */
  if (defs === null && !fetchErr) {
    return (
      <div className="nx-cf-panel nx-cf-panel--loading">
        {[0, 1, 2].map(i => (
          <div key={i} className="nx-cf-skeleton">
            <div className="nx-skeleton-line" style={{ width: '40%', marginBottom: 6 }} />
            <div className="nx-skeleton-line" style={{ width: '70%' }} />
          </div>
        ))}
      </div>
    );
  }

  /* Error */
  if (fetchErr) {
    return (
      <div className="nx-cf-panel">
        <div className="nx-empty">
          <NexusIcon name="AlertCircle" size={18} />
          <span>Could not load custom fields.</span>
        </div>
      </div>
    );
  }

  /* Empty */
  if (!defs.length) {
    return (
      <div className="nx-cf-panel">
        <div className="nx-empty">
          <NexusIcon name="Sliders" size={18} />
          <span>No custom fields configured. Administrators can add them in Settings → Custom Fields.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={'nx-cf-panel' + (saving ? ' is-saving' : '')}>
      {defs.map(f => (
        <CfRow key={f.id} field={f} raw={values[f.id] ?? null}
          onSave={v => handleSave(f.id, v)} disabled={saving} />
      ))}
    </div>
  );
};
window.CustomFieldsPanel = CustomFieldsPanel;

/* ================================================================
   EMPTY STATE — rich zero-data state for lists/tabs
   ================================================================ */
const EmptyState = ({ icon = 'Inbox', title, desc, action, compact = false }) => (
  <div className={'nx-empty-state' + (compact ? ' is-compact' : '')}>
    <div className="nx-empty-state-icon">
      <NexusIcon name={icon} size={compact ? 18 : 28} />
    </div>
    {title && <span className="nx-empty-state-title">{title}</span>}
    {desc  && <span className="nx-empty-state-desc">{desc}</span>}
    {action && <div className="nx-empty-state-action">{action}</div>}
  </div>
);
window.EmptyState = EmptyState;

/* ================================================================
   SKELETON ROW — loading placeholder
   ================================================================ */
const SkeletonRow = ({ lines = 2, wide = false }) => (
  <div className="nx-skeleton-row" aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="nx-skeleton-line" style={{ width: wide ? '100%' : i === 0 ? '60%' : '40%' }} />
    ))}
  </div>
);
window.SkeletonRow = SkeletonRow;

/* ================================================================
   ARCHIVE / RESTORE BUTTON
   Handles archive + restore for people / companies with confirmation.
   ================================================================ */
const ArchiveRestoreBtn = ({ record, type, onDone }) => {
  const store = useStore();
  const toast = useToast();
  const isArchived = !!record.archivedAt;
  const label = isArchived ? 'Restore' : 'Archive';
  const icon  = isArchived ? 'ArchiveRestore' : 'Archive';

  const handleClick = () => {
    if (isArchived) {
      store.update(type, record.id, { archivedAt: null });
      toast({ tone: 'success', icon: 'ArchiveRestore', title: (record.name || 'Record') + ' restored' });
      onDone?.();
    } else {
      store.confirm({
        title:       'Archive ' + (record.name || 'this record') + '?',
        description: 'The record will be hidden from active lists but not deleted. You can restore it later.',
        danger:      false,
        onConfirm:   () => {
          store.update(type, record.id, { archivedAt: new Date().toISOString() });
          toast({ tone: 'success', icon: 'Archive', title: (record.name || 'Record') + ' archived' });
          store.closeDrawer();
          onDone?.();
        },
      });
    }
  };

  return (
    <button className="nx-chip-menu-item" onClick={handleClick}>
      <NexusIcon name={icon} size={11} />
      <span>{label}</span>
    </button>
  );
};
window.ArchiveRestoreBtn = ArchiveRestoreBtn;

/* ================================================================
   AI ENTITY CARD — AI insight placeholder for Companies + Deals
   ================================================================ */
const AIEntityCard = ({ record, entityType }) => {
  const store = useStore();
  const score = record.score || record.probability || 0;
  const scoreColor = score >= 70 ? 'var(--nx-success)' : score >= 40 ? '#F59E0B' : 'var(--nx-danger)';

  const insight = entityType === 'companies'
    ? {
        label:  'Account Health',
        signal: score >= 70 ? 'Strong engagement signals detected.' : score >= 40 ? 'Moderate engagement — nurture recommended.' : 'At-risk account — immediate attention needed.',
        action: score >= 70 ? 'Propose upsell or expansion deal.' : 'Schedule a QBR to re-engage key contacts.',
      }
    : {
        label:  'Win Probability',
        signal: score >= 70 ? 'High confidence in deal progression.' : score >= 40 ? 'Some risk factors detected — review stage.' : 'Deal showing stall signals — follow up now.',
        action: score >= 70 ? 'Prepare proposal and close plan.' : 'Create follow-up task and re-qualify next steps.',
      };

  return (
    <div className="nx-ai-compact">
      <div className="nx-ai-compact-head">
        <NexusIcon name="Sparkles" size={11} />
        <span>AI Insights</span>
        <span className="nx-ai-compact-badge">{(store?.branding?.brandName || 'AI') + ' AI'}</span>
      </div>
      <div className="nx-ai-compact-row">
        <div className="nx-ai-compact-cell">
          <span className="nx-ai-compact-num" style={{ color: scoreColor }}>{score}</span>
          <div className="nx-ai-compact-bar"><div style={{ width: score + '%', background: scoreColor }} /></div>
          <span className="nx-ai-compact-lbl">{insight.label}</span>
        </div>
        <div className="nx-ai-compact-cell" style={{ flex: 2 }}>
          <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', lineHeight: 1.4 }}>{insight.signal}</span>
        </div>
      </div>
      <div className="nx-ai-compact-nba">
        <NexusIcon name="Zap" size={10} />
        <span>{insight.action}</span>
      </div>
    </div>
  );
};
window.AIEntityCard = AIEntityCard;

/* ================================================================
   STAGE HISTORY — timeline of stage changes for a deal
   ================================================================ */
const StageHistoryPanel = ({ record, stages = [] }) => {
  const stageHistory = useMemoU(() => {
    const history = record.stageHistory || [];
    if (!history.length) {
      const current = stages.find(s => s.id === record.stageId);
      if (!current) return [];
      return [{ stageId: record.stageId, stageName: current.name, stageTone: current.tone, enteredAt: record.createdAt, daysInStage: record.age || 0, isCurrent: true }];
    }
    return history.map((h, i) => {
      const stage = stages.find(s => s.id === h.stageId);
      return {
        ...h,
        stageName: stage?.name || h.stageName || '—',
        stageTone: stage?.tone || null,
        isCurrent: i === history.length - 1,
      };
    });
  }, [record, stages]);

  if (!stageHistory.length) return (
    <div className="nx-drawer-page">
      <EmptyState icon="TrendingUp" title="No stage history" desc="Stage transitions will appear here." compact />
    </div>
  );

  return (
    <div className="nx-drawer-page">
      <ol className="nx-timeline">
        {stageHistory.map((h, i) => (
          <li key={`${h.stageId}-${h.enteredAt || i}`} className="nx-timeline-item">
            <span className="nx-timeline-dot" style={{
              background: h.isCurrent ? (h.stageTone || 'var(--nx-accent)') : 'var(--nx-surface-alt)',
              color: h.isCurrent ? '#fff' : 'var(--nx-text-muted)',
            }}>
              <NexusIcon name={h.isCurrent ? 'MapPin' : 'Check'} size={10} />
            </span>
            <div className="nx-timeline-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between', minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span className="nx-timeline-text" style={{ fontWeight: 700 }}>{h.stageName}</span>
                  {h.isCurrent && <Pill tone="blue">Current</Pill>}
                </span>
                {h.enteredAt && (
                  <span className="nx-timeline-time" style={{ flexShrink: 0 }}>
                    {new Date(h.enteredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              {h.daysInStage != null && (
                <span className="nx-timeline-detail">
                  {h.daysInStage === 0 ? 'Entered today' : `${h.daysInStage}d in this stage`}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};
window.StageHistoryPanel = StageHistoryPanel;

/* ================================================================
   FORECAST PANEL — deal revenue forecast placeholder
   ================================================================ */
const ForecastPanel = ({ record, stage }) => {
  const probability = record.probability || stage?.prob || 0;
  const value = record.value || 0;
  const weighted = Math.round(value * probability / 100);
  const isWon = stage?.kind === 'WON' || /\bwon\b/i.test(stage?.name || '');
  const isLost = stage?.kind === 'LOST' || /\blost\b/i.test(stage?.name || '');
  const fmt = n => '$' + (n >= 1000 ? (n/1000).toFixed(1) + 'K' : n.toLocaleString());
  const tone = isWon ? 'green' : isLost ? 'red' : probability >= 70 ? 'green' : probability >= 40 ? 'amber' : 'gray';

  return (
    <div className="nx-crm-snapshot">
      <div className="nx-crm-row">
        <span className="nx-crm-lbl">Deal Value</span>
        <span className="nx-crm-val" style={{ fontWeight: 800, color: 'var(--nx-accent)' }}>{fmt(value)}</span>
      </div>
      <div className="nx-crm-row">
        <span className="nx-crm-lbl">Win Probability</span>
        <span className="nx-crm-val">
          {isWon ? <Pill tone="green">Won</Pill> : isLost ? <Pill tone="red">Lost</Pill> : `${probability}%`}
        </span>
      </div>
      <div className="nx-crm-row">
        <span className="nx-crm-lbl">Weighted Revenue</span>
        <span className="nx-crm-val" style={{ fontWeight: 700, color: isWon ? 'var(--nx-success)' : 'var(--nx-text)' }}>{fmt(weighted)}</span>
      </div>
      <div className="nx-crm-row">
        <span className="nx-crm-lbl">Close Date</span>
        <span className="nx-crm-val">{record.closeDate ? new Date(record.closeDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
      </div>
      <div className="nx-crm-row">
        <span className="nx-crm-lbl">Forecast Category</span>
        <Pill tone={tone}>{isWon ? 'Closed Won' : isLost ? 'Closed Lost' : probability >= 70 ? 'Commit' : probability >= 40 ? 'Best Case' : 'Pipeline'}</Pill>
      </div>
    </div>
  );
};
window.ForecastPanel = ForecastPanel;

/* ================================================================
   DUPLICATE DETECTION HOOK — fired during person create
   ================================================================ */
const useDuplicateDetection = (name, email) => {
  const store = useStore();
  return useMemoU(() => {
    if (!name && !email) return [];
    const q = (name || '').toLowerCase();
    const e = (email || '').toLowerCase();
    return store.people.filter(p =>
      (q.length > 2 && (p.name || '').toLowerCase().includes(q)) ||
      (e.length > 4 && (p.email || '').toLowerCase() === e)
    ).slice(0, 3);
  }, [name, email, store.people]);
};
window.useDuplicateDetection = useDuplicateDetection;

/* ================================================================
   DUPLICATE BANNER — shown in person create form when matches found
   ================================================================ */
const DuplicateBanner = ({ duplicates, onOpenRecord }) => {
  if (!duplicates?.length) return null;
  return (
    <div className="nx-duplicate-banner">
      <NexusIcon name="AlertCircle" size={12} />
      <span>Possible duplicate{duplicates.length > 1 ? 's' : ''} found:</span>
      {duplicates.slice(0, 2).map(d => (
        <button key={d.id} className="nx-duplicate-chip" onClick={() => onOpenRecord?.(d)}>
          {d.name}
          {d.email && <span className="nx-muted"> · {d.email}</span>}
        </button>
      ))}
    </div>
  );
};
window.DuplicateBanner = DuplicateBanner;
